import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'

// ---------------------------------------------------------------------------
// Tipi
// ---------------------------------------------------------------------------

export interface Order {
  id: string
  external_id: string | null
  order_date: string | null
  customer_name: string | null
  order_value: number | null
  currency: string
  status: string
  service_line: string | null
  budget_hours: number | null
  budget_cost: number | null
  description: string | null
  created_at: string | null
  updated_at: string | null
}

export interface ProductionSummary {
  total_hours: number
  total_cost: number
  customer_hours: number
  rd_hours: number
  cost_variance: number | null
}

export interface OrderDetail extends Order {
  notes: string | null
  production: ProductionSummary
}

export interface OrdersListResponse {
  total: number
  items: Order[]
}

export interface OrdersFilters {
  status?: string
  customer_name?: string
  from_date?: string
  to_date?: string
  service_line?: string
  business_unit_id?: string
  search?: string
  limit?: number
  offset?: number
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useOrders(filters: OrdersFilters = {}) {
  const params = new URLSearchParams()
  if (filters.status) params.set('status', filters.status)
  if (filters.customer_name) params.set('customer_name', filters.customer_name)
  if (filters.from_date) params.set('from_date', filters.from_date)
  if (filters.to_date) params.set('to_date', filters.to_date)
  if (filters.service_line) params.set('service_line', filters.service_line)
  if (filters.business_unit_id) params.set('business_unit_id', filters.business_unit_id)
  if (filters.search) params.set('search', filters.search)
  params.set('limit', String(filters.limit ?? 50))
  params.set('offset', String(filters.offset ?? 0))

  const qs = params.toString()
  return useQuery<OrdersListResponse>({
    queryKey: ['orders', qs],
    queryFn: () => api.get<OrdersListResponse>(`/api/v1/orders?${qs}`),
  })
}

export function useOrderDetail(orderId: string | null) {
  return useQuery<OrderDetail>({
    queryKey: ['order', orderId],
    queryFn: () => api.get<OrderDetail>(`/api/v1/orders/${orderId}`),
    enabled: !!orderId,
  })
}
